import React from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, CheckCircle, MapPin } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const getStatusSteps = (status) => {
  const allSteps = [
    { id: 'pending', label: 'Order Placed', icon: Package },
    { id: 'confirmed', label: 'Confirmed', icon: CheckCircle },
    { id: 'shipped', label: 'Shipped', icon: Truck },
    { id: 'delivered', label: 'Delivered', icon: MapPin }
  ];

  const statusOrder = ['pending', 'confirmed', 'shipped', 'delivered', 'canceled'];
  const currentIndex = statusOrder.indexOf(status);

  return allSteps.map((step, index) => ({
    ...step,
    completed: index < currentIndex || (status === step.id),
    active: status === step.id,
    canceled: status === 'canceled'
  }));
};

export const OrderTimeline = ({ order }) => {
  const steps = getStatusSteps(order.status);

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="font-serif text-2xl font-semibold mb-6">Order Status</h3>
        
        {order.status === 'canceled' ? (
          <div className="text-center py-8">
            <Badge variant="destructive" className="text-lg px-6 py-2">Order Canceled</Badge>
            <p className="text-muted-foreground mt-4">This order has been canceled</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-border" />
            
            <div className="space-y-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative flex items-start gap-4"
                  >
                    {/* Icon Circle */}
                    <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center transition-smooth ${
                      step.completed
                        ? 'bg-primary text-primary-foreground'
                        : step.active
                        ? 'bg-primary/20 text-primary border-2 border-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 pt-2">
                      <h4 className={`font-semibold mb-1 ${
                        step.completed || step.active ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {step.label}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {step.completed
                          ? 'Completed'
                          : step.active
                          ? 'In Progress'
                          : 'Pending'}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};